import React, { useEffect } from 'react';
import './VideoModal.css';

interface VideoModalProps {
  videoUrl: string;
  onClose: () => void;
  title?: string;
}

const VideoModal: React.FC<VideoModalProps> = ({ videoUrl, onClose, title }) => {
  // Prevent scrolling of the background when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // Re-enable scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Close modal when clicking outside the video
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Generate appropriate embed URL based on video URL
  const getEmbedUrl = (url: string): string => {
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
      const match = url.match(youtubeRegex);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`;
      }
    }

    // Vimeo
    else if (url.includes('vimeo.com')) {
      const vimeoRegex = /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^/]*)\/videos\/|album\/(?:\d+)\/video\/|)(\d+)(?:$|\/|\?)/;
      const match = url.match(vimeoRegex);
      if (match && match[1]) {
        return `https://player.vimeo.com/video/${match[1]}?autoplay=1`;
      }
    }

    // Facebook
    else if (url.includes('facebook.com/')) {
      if (url.includes('/videos/')) {
        return url.replace('facebook.com/watch/?v=', 'facebook.com/plugins/video.php?href=https://facebook.com/watch/?v=') + '&autoplay=true';
      }
    }

    // If we can't process it as an embeddable URL, return the original
    // This will be used in a fallback player or direct link
    return url;
  };

  const embedUrl = getEmbedUrl(videoUrl);
  const isDirectVideoUrl = videoUrl.match(/\.(mp4|webm|ogg)$/i);

  return (
    <div className="video-modal-backdrop" onClick={handleBackdropClick}>
      <div className="video-modal-content">
        <button className="video-modal-close" onClick={onClose} aria-label="Close video">×</button>

        {title && <h3 className="video-modal-title">{title}</h3>}

        <div className="video-container">
          {isDirectVideoUrl ? (
            // For direct video files (.mp4, etc)
            <video
              controls
              autoPlay
              className="video-player"
              src={videoUrl}
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            // For embeddable videos (YouTube, Vimeo, etc)
            <iframe
              src={embedUrl}
              title={title || "Product Video"}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="video-iframe"
            ></iframe>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoModal;